import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { listOpenProcedureItems } from '../../services/api/patientPlanApi';
import useProcedureStore from '../../stores/procedureStore';
import { fetchStaffList } from '../../services/api/staffApi';
import { excludeLabTests } from '../../constants/procedures';
import { procedurePickerDeptAnchor } from './departmentResolution';

/**
 * RegistrationProceduresSection — Procedures-on-New-Visits.
 *
 * Lets the receptionist process a returning patient's procedures right on the
 * Registration page:
 *   - doctor-suggested PENDING procedures for the patient (dropdown), each
 *     showing the procedure name + suggesting doctor + suggested date, with a
 *     "Standing" / "Already paid" badge. Suggestions past their expiry date are
 *     already filtered out by the backend.
 *   - procedures booked on the matched APPOINTMENT are auto-selected.
 *   - the receptionist can add catalog procedures flagged Receptionist-allowed.
 *   - each newly-added procedure can be assigned to a nurse (when the procedure
 *     is nurse/receptionist allowed) instead of the doctor.
 *
 * Emits `onChange(lines, feeTotal)` where `lines` are the `sitting_moves` the
 * registration bundled-payment call consumes:
 *   pay_and_move  — unpaid suggestion: collect + move onto the new visit
 *   move_only     — paid-but-unperformed: carry forward, no new charge
 *   create_new    — fresh catalog / appointment procedure created on the visit
 *
 * S2.1/C1 — each checked row (except move_only) carries a Qty stepper
 * (default 1, min 1, integer). `create_new`/`pay_and_move` lines emit
 * `quantity` + `amount = unit × quantity`; `feeTotal` sums the qty-adjusted
 * amounts — the ONE total RegisterVisitForm's fee panel/grand total reads
 * (no re-derivation there). `move_only` lines never carry `quantity` — the
 * server ignores qty on an already-paid carry-forward (billed once).
 *
 * Paid-but-unperformed rows are unselected by default and fully toggleable: tick to
 * bring (move) the procedure onto this new visit, or leave it to keep it on its
 * original visit (it stays available for next time). When the patient has ANY paid
 * procedures, a popup asks the receptionist which to add — so nothing is ever added
 * silently. No re-charge on move.
 */
export default function RegistrationProceduresSection({
  patientId,
  departmentId = '',
  visitProviderType,
  doctorId,
  doctorName,
  defaultNurseId = '',
  appointment,
  onChange,
}) {
  const { isDark } = useTheme();
  // Full admin catalog — used to RESOLVE names/flags of already-advised /
  // appointment-booked procedures (which may be tagged to another department;
  // A4/S3 must never HIDE an already-added item).
  const { procedures, fetchProcedures } = useProcedureStore();
  // A4/S3 — the "+ Add a procedure…" catalog picker is dept-scoped to the
  // department selected on this registration (server-scoped, dept-tagged ∪
  // org-wide). Falsy/EMERGENCY anchor → null → full list.
  const pickerProcedures = useProcedureStore((s) => s.pickerProcedures);
  const fetchProceduresForDept = useProcedureStore((s) => s.fetchProceduresForDept);
  const pickerDeptAnchor = procedurePickerDeptAnchor({ department_id: departmentId });
  const [pending, setPending] = useState([]);     // enriched open items
  const [nurses, setNurses] = useState([]);
  const [adds, setAdds] = useState([]);           // catalog procedure_ids added
  // selection: key -> { checked, assigneeId } ; key = sitting_id or `new:<procId>`
  const [selection, setSelection] = useState({});
  const [loading, setLoading] = useState(false);
  const [catalogPick, setCatalogPick] = useState('');
  const [paidPrompt, setPaidPrompt] = useState(null); // { items, picks } — "add paid procedures?" popup
  const promptedPatientRef = useRef(null);            // patientId already prompted (once per patient)

  // ── Load pending suggestions + catalog + nurses ──
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [openRes] = await Promise.all([
          patientId ? listOpenProcedureItems(patientId) : Promise.resolve({ data: { items: [] } }),
          procedures.length ? Promise.resolve() : fetchProcedures(),
        ]);
        if (cancelled) return;
        // listOpenProcedureItems returns a { success, data: { items } } envelope.
        const items = (openRes?.data?.items) || openRes?.items || [];
        setPending(items);
        // Auto-select defaults are computed in the effect below (keyed on the loaded
        // items + the chosen visit doctor) so changing the doctor re-evaluates the
        // defaults without re-fetching.
      } catch (_) {
        if (!cancelled) setPending([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [patientId, appointment, fetchProcedures, procedures.length]);

  // ── Scoped catalog for the "+ Add a procedure…" picker ──
  // Keyed on the (primitive) dept anchor; store fetch is single-flight so
  // changing the department during registration can't storm requests.
  useEffect(() => {
    fetchProceduresForDept(pickerDeptAnchor);
  }, [pickerDeptAnchor, fetchProceduresForDept]);

  // ── Auto-select appointment-booked procedures (no re-fetch) ──
  // Procedures the patient explicitly booked on the matched appointment are ticked.
  // Paid-but-unperformed rows are NOT auto-ticked — they are offered via the popup
  // below so nothing is added silently; everything else stays UNSELECTED by default.
  useEffect(() => {
    if (!pending.length) return;
    const apptProcIds = new Set(
      (appointment?.procedures || []).map((p) => p.procedure_id).filter(Boolean),
    );
    if (!apptProcIds.size) return;
    const initial = {};
    for (const it of pending) {
      if (it.procedure_id && apptProcIds.has(it.procedure_id)) {
        const key = it.sitting_id || `new:${it.procedure_id}`;
        // Newly-created appointment procedures default to the visit nurse in Nurse
        // Visit mode; carried-forward (sitting_id) rows keep their existing assignee.
        initial[key] = { checked: true, assigneeId: it.sitting_id ? '' : (defaultNurseId || '') };
      }
    }
    if (Object.keys(initial).length) setSelection((prev) => ({ ...initial, ...prev }));
  }, [pending, appointment, defaultNurseId]);

  useEffect(() => {
    let cancelled = false;
    fetchStaffList()
      .then((roster) => {
        if (cancelled) return;
        // fetchStaffList returns a { success, data } envelope (data = member array).
        const list = Array.isArray(roster)
          ? roster
          : (roster?.data || roster?.staff || roster?.members || []);
        setNurses(list.filter((m) => String(m.role || '').toUpperCase() === 'NURSE'));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const catalogById = useMemo(() => {
    const m = {};
    for (const p of procedures || []) m[p.procedure_id] = p;
    return m;
  }, [procedures]);

  // Receptionist-addable catalog rows not already pending / added. Sourced from
  // the dept-SCOPED picker slice (A4/S3), so only procedures in the selected
  // department (∪ org-wide) are offered for adding.
  const addableCatalog = useMemo(() => {
    const pendingIds = new Set(pending.map((p) => p.procedure_id));
    const addedIds = new Set(adds);
    return excludeLabTests(pickerProcedures).filter(
      (p) => p.receptionist_allowed && p.active !== false
        && !pendingIds.has(p.procedure_id) && !addedIds.has(p.procedure_id),
    );
  }, [pickerProcedures, pending, adds]);

  // Combined rows: pending suggestions + receptionist catalog adds.
  const rows = useMemo(() => {
    // OWNER RULING: the picker gates on `receptionist_allowed` — the same catalog
    // flag the addable catalog uses at :146-153 — mirroring the lab picker
    // (addableLabCatalog). Eligibility (who may perform the procedure, e.g. a
    // nurse) is resolved server-side at bundled-payment time; the FE has no
    // opinion on it. Procedures missing from the catalog are treated as
    // not-receptionist-allowed.
    // A DOCTOR-attended OTC visit (visit_type NURSE + a chosen doctor, W3 XOR)
    // behaves like an OPD visit for the pending picker: the attending doctor's
    // advised procedures all show (no receptionist_allowed narrowing). Only a
    // nurse-attended OTC applies the receptionist_allowed gate.
    const otcDoctorAttendee = visitProviderType === 'NURSE' && !!doctorId;
    const visiblePending = (visitProviderType === 'NURSE' && !otcDoctorAttendee)
      ? pending.filter((it) => catalogById[it.procedure_id]?.receptionist_allowed === true)
      : pending;
    const out = visiblePending.map((it) => ({
      key: it.sitting_id || `new:${it.procedure_id}`,
      procedure_id: it.procedure_id,
      name: it.name,
      final_cost: Number(it.final_cost || 0),
      sitting_id: it.sitting_id,
      plan_id: it.plan_id,
      standing: !!it.standing,
      expires_at: it.expires_at || null,
      paid: it.payment_status === 'paid',
      doctor: it.suggesting_doctor_name,
      date: it.suggested_visit_date,
      nurse_ok: false, // pending suggestions keep their existing assignee
      source: it.sitting_id ? 'pending' : 'new',
    }));
    for (const pid of adds) {
      const p = catalogById[pid];
      if (!p) continue;
      out.push({
        key: `new:${pid}`,
        procedure_id: pid,
        name: p.name,
        final_cost: Number(p.default_cost || 0),
        sitting_id: null,
        plan_id: null,
        standing: false, // standing is a per-advice choice, not a catalog default
        expires_at: null,
        paid: false,
        doctor: null,
        date: null,
        // Nurse picker is for receptionist-specific procedures. `nurse_allowed`
        // is a separate concept (a nurse initiating a procedure) and is not used here.
        nurse_ok: !!p.receptionist_allowed,
        source: 'new',
      });
    }
    return out;
  }, [pending, adds, catalogById, visitProviderType, doctorId]);

  // ── Emit lines + fee total whenever selection / rows change ──
  // C1 (W2 qty): every create_new / pay_and_move line carries `quantity`
  // (int ≥1; the row's stepper, default 1) and its `amount` = qty × unit —
  // this IS the single source `feeTotal` the registration fee panel reads
  // (RegisterVisitForm just sums it in, no re-derivation). move_only lines
  // (already-paid carry-forward) never carry quantity — server ignores it.
  useEffect(() => {
    const lines = [];
    let feeTotal = 0;
    for (const r of rows) {
      const sel = selection[r.key];
      if (!sel?.checked) continue;
      // The suggesting doctor — surfaced so the registration token can show
      // "Advised by: Doctor" + the name for a Nurse Visit (null for fresh
      // receptionist-added catalog procedures, which no doctor advised).
      const advisedByDoctor = { suggesting_doctor_name: r.doctor || null };
      if (r.source === 'new') {
        const qty = sel.qty || 1;
        const lineAmount = r.final_cost * qty;
        lines.push({
          action: 'create_new',
          procedure_id: r.procedure_id,
          // F13 — carry the procedure name so the registration token's
          // race-free money derivation (tokenMoneyFromCollect) can itemize
          // without a receipts read-back. Display-only; server ignores it.
          name: r.name,
          amount: lineAmount,
          quantity: qty,
          ...advisedByDoctor,
          ...(sel.assigneeId
            ? { assigned_to_member_id: sel.assigneeId, assigned_to_role: 'NURSE' }
            : {}),
        });
        feeTotal += lineAmount;
      } else if (r.paid) {
        lines.push({ action: 'move_only', plan_id: r.plan_id, sitting_id: r.sitting_id, name: r.name, ...advisedByDoctor });
        // no charge — carried forward already paid; quantity ignored server-side
      } else {
        const qty = sel.qty || 1;
        const lineAmount = r.final_cost * qty;
        lines.push({
          action: 'pay_and_move', plan_id: r.plan_id, sitting_id: r.sitting_id,
          name: r.name, amount: lineAmount, quantity: qty, ...advisedByDoctor,
        });
        feeTotal += lineAmount;
      }
    }
    onChange?.(lines, feeTotal);
  }, [rows, selection, onChange]);

  // ── "Add paid procedures?" popup ──
  // Whenever the patient has ANY paid-but-unperformed procedures, surface them once
  // (per patient) in a popup so the receptionist explicitly chooses which to bring —
  // they are never added silently. Unchosen ones stay on their original visit.
  useEffect(() => {
    if (!pending.length) return;
    if (promptedPatientRef.current === patientId) return;
    const paidItems = rows.filter((r) => r.source === 'pending' && r.paid);
    if (paidItems.length) {
      promptedPatientRef.current = patientId;
      setPaidPrompt({ items: paidItems, picks: {} });
    }
  }, [pending, patientId, rows]);

  const togglePaidPick = useCallback((key) => {
    setPaidPrompt((p) => (p ? { ...p, picks: { ...p.picks, [key]: !p.picks[key] } } : p));
  }, []);
  const dismissPaidPrompt = useCallback(() => setPaidPrompt(null), []);
  const confirmPaidPrompt = useCallback(() => {
    if (!paidPrompt) return;
    setSelection((prev) => {
      const next = { ...prev };
      for (const it of paidPrompt.items) {
        if (paidPrompt.picks[it.key]) next[it.key] = { ...(next[it.key] || {}), checked: true };
      }
      return next;
    });
    setPaidPrompt(null);
  }, [paidPrompt]);

  const toggle = useCallback((key) => {
    setSelection((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), checked: !prev[key]?.checked },
    }));
  }, []);

  const setAssignee = useCallback((key, assigneeId) => {
    setSelection((prev) => ({ ...prev, [key]: { ...(prev[key] || { checked: true }), assigneeId } }));
  }, []);

  // Qty stepper (S2.1) — default 1, editable, integer ≥1. `bumpQty` (+/-
  // buttons) always lands on a valid integer by construction. `setQty` (the
  // typed input) rejects 0/blank/negative/non-numeric by falling back to the
  // row's current quantity — the input never goes below 1.
  const bumpQty = useCallback((key, delta) => {
    setSelection((prev) => {
      const current = prev[key] || { checked: true };
      const qty = Math.max(1, (current.qty || 1) + delta);
      return { ...prev, [key]: { ...current, qty } };
    });
  }, []);
  const setQty = useCallback((key, raw) => {
    setSelection((prev) => {
      const current = prev[key] || { checked: true };
      const n = parseInt(raw, 10);
      const qty = Number.isInteger(n) && n >= 1 ? n : (current.qty || 1);
      return { ...prev, [key]: { ...current, qty } };
    });
  }, []);

  const addCatalog = useCallback((pid) => {
    if (!pid) return;
    setAdds((prev) => (prev.includes(pid) ? prev : [...prev, pid]));
    // Nurse Visit: default the new procedure's assignee to the visit nurse so its
    // bundled-payment line carries assigned_to_member_id + assigned_to_role=NURSE.
    setSelection((prev) => ({ ...prev, [`new:${pid}`]: { checked: true, assigneeId: defaultNurseId || '' } }));
    setCatalogPick('');
  }, [defaultNurseId]);

  if (loading && rows.length === 0) {
    return (
      <div style={{ fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280', padding: '0.5rem 0' }}>
        Loading procedures…
      </div>
    );
  }

  if (rows.length === 0 && addableCatalog.length === 0) return null;

  const card = {
    borderRadius: '0.75rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : '#cbd5e1'}`,
    background: isDark ? '#1e293b' : '#f8fafc',
    padding: '0.875rem',
    marginTop: '0.75rem',
  };
  const chip = (bg, text) => ({
    fontSize: '0.625rem', fontWeight: 600, padding: '0.0625rem 0.375rem',
    borderRadius: '9999px', background: bg, color: text, marginLeft: '0.375rem',
  });

  return (
    <>
    <div style={card}>
      <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', marginBottom: '0.5rem' }}>
        🩺 Procedures for this visit
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {rows.map((r) => {
          const sel = selection[r.key];
          const checked = !!sel?.checked;
          const qty = sel?.qty || 1;
          // Qty applies to create_new + pay_and_move lines only — an
          // already-paid pending row (move_only) is billed once and the
          // stepper stays hidden (C1: quantity ignored server-side for
          // move_only carry-forward).
          const showQty = checked && !r.paid;
          return (
            <div key={r.key} style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
              padding: '0.375rem 0.5rem', borderRadius: '0.5rem',
              background: checked ? (isDark ? 'rgba(13,148,136,0.18)' : '#ccfbf1') : 'transparent',
            }}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(r.key)}
                style={{ marginTop: '0.1875rem' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.8125rem', color: isDark ? '#f1f5f9' : '#0f172a' }}>
                  {r.name}
                  {r.standing && <span style={chip(isDark ? 'rgba(139,92,246,0.25)' : '#ede9fe', isDark ? '#c4b5fd' : '#6d28d9')}>Standing{r.expires_at ? ` · till ${r.expires_at}` : ''}</span>}
                  {r.paid && <span style={chip(isDark ? 'rgba(34,197,94,0.25)' : '#dcfce7', isDark ? '#86efac' : '#166534')}>Already paid</span>}
                </div>
                {(r.doctor || r.date) && (
                  <div style={{ fontSize: '0.6875rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                    {r.doctor ? `Dr. ${r.doctor}` : 'Doctor'}{r.date ? ` · ${r.date}` : ''}
                  </div>
                )}
                {/* Nurse / doctor assignee for newly-added procedures */}
                {checked && r.source === 'new' && r.nurse_ok && nurses.length > 0 && (
                  <select
                    value={sel?.assigneeId || ''}
                    onChange={(e) => setAssignee(r.key, e.target.value)}
                    style={{
                      marginTop: '0.25rem', fontSize: '0.6875rem', padding: '0.1875rem 0.375rem',
                      borderRadius: '0.375rem',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}`,
                      background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
                    }}
                  >
                    <option value="">{doctorName ? `Dr. ${doctorName}` : 'Doctor'}</option>
                    {nurses.map((n) => {
                      // Members carry first_name/last_name (not a single `name`),
                      // matching the canonical displayName fallback chain.
                      const nm = `${(n.first_name || '').trim()} ${(n.last_name || '').trim()}`.trim()
                        || n.full_name || n.name || n.email || 'Nurse';
                      return (
                        <option key={n.member_id} value={n.member_id}>Nurse · {nm}</option>
                      );
                    })}
                  </select>
                )}
              </div>
              {showQty && (
                // Fixed-width, flexShrink:0 so a long procedure name (name
                // div is flex:1/minWidth:0, S2.1 AC5 long-list note) never
                // pushes the stepper out of the row.
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
                  <button
                    type="button"
                    aria-label={`Decrease quantity for ${r.name}`}
                    onClick={() => bumpQty(r.key, -1)}
                    disabled={qty <= 1}
                    style={{
                      width: '1.25rem', height: '1.25rem', borderRadius: '0.25rem', lineHeight: 1,
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1'}`,
                      background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
                      cursor: qty <= 1 ? 'not-allowed' : 'pointer', opacity: qty <= 1 ? 0.5 : 1,
                    }}
                  >−</button>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    value={qty}
                    aria-label={`Quantity for ${r.name}`}
                    onChange={(e) => setQty(r.key, e.target.value)}
                    style={{
                      width: '2rem', textAlign: 'center', fontSize: '0.75rem', padding: '0.125rem 0',
                      borderRadius: '0.25rem',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1'}`,
                      background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
                    }}
                  />
                  <button
                    type="button"
                    aria-label={`Increase quantity for ${r.name}`}
                    onClick={() => bumpQty(r.key, 1)}
                    style={{
                      width: '1.25rem', height: '1.25rem', borderRadius: '0.25rem', lineHeight: 1,
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1'}`,
                      background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
                      cursor: 'pointer',
                    }}
                  >+</button>
                </div>
              )}
              <div style={{ fontSize: '0.75rem', fontWeight: 600, color: r.paid ? (isDark ? '#86efac' : '#16a34a') : (isDark ? '#f1f5f9' : '#0f172a') }}>
                {r.paid ? 'Paid' : `₹${r.final_cost * (showQty ? qty : 1)}`}
              </div>
            </div>
          );
        })}
      </div>

      {addableCatalog.length > 0 && (
        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
          <select
            value={catalogPick}
            onChange={(e) => addCatalog(e.target.value)}
            style={{
              flex: 1, fontSize: '0.75rem', padding: '0.375rem 0.5rem', borderRadius: '0.5rem',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : '#cbd5e1'}`,
              background: isDark ? '#0f172a' : '#fff', color: isDark ? '#f1f5f9' : '#0f172a',
            }}
          >
            <option value="">+ Add a procedure…</option>
            {addableCatalog.map((p) => (
              <option key={p.procedure_id} value={p.procedure_id}>
                {p.name} — ₹{p.default_cost}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>

    {paidPrompt && (
      <div
        role="dialog"
        aria-modal="true"
        onClick={dismissPaidPrompt}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000, padding: '1rem',
          background: 'rgba(15,23,42,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: '24rem', borderRadius: '0.75rem', padding: '1rem',
            background: isDark ? '#1e293b' : '#ffffff',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#cbd5e1'}`,
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ fontSize: '0.9375rem', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>
            Already-paid procedures
          </div>
          <div style={{ fontSize: '0.8125rem', color: isDark ? '#cbd5e1' : '#475569', margin: '0.375rem 0 0.75rem' }}>
            This patient has paid procedures from before. Tick the ones to bring onto this
            visit — anything you leave unticked stays on its original visit.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', maxHeight: '14rem', overflowY: 'auto' }}>
            {paidPrompt.items.map((it) => (
              <label key={it.key} style={{
                display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer',
                padding: '0.375rem 0.5rem', borderRadius: '0.5rem',
                background: isDark ? '#0f172a' : '#f8fafc',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0'}`,
              }}>
                <input
                  type="checkbox"
                  checked={!!paidPrompt.picks[it.key]}
                  onChange={() => togglePaidPick(it.key)}
                  style={{ marginTop: '0.1875rem' }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.8125rem', color: isDark ? '#f1f5f9' : '#0f172a' }}>{it.name}</div>
                  {(it.doctor || it.date) && (
                    <div style={{ fontSize: '0.6875rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                      {it.doctor ? `Dr. ${it.doctor}` : 'Doctor'}{it.date ? ` · ${it.date}` : ''}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? '#86efac' : '#16a34a' }}>Paid</div>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.875rem' }}>
            <button
              type="button"
              onClick={dismissPaidPrompt}
              style={{
                padding: '0.375rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.8125rem', fontWeight: 600,
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.18)' : '#cbd5e1'}`,
                background: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#e2e8f0' : '#334155',
                cursor: 'pointer',
              }}
            >Keep all unselected</button>
            <button
              type="button"
              onClick={confirmPaidPrompt}
              style={{
                padding: '0.375rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.8125rem', fontWeight: 700,
                border: '1px solid #0d9488', background: '#0d9488', color: '#ffffff', cursor: 'pointer',
              }}
            >Add selected</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
